import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  Tab,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import React, { FC, useState } from 'react'
import uuid from 'react-uuid'
import {
  CURRENCY,
  IProductExtras,
  IProductInstructions,
  IProductRate,
  ProductType,
} from '../../../all-types'
import { CommonBtn } from '../../../components'
import { NO_IMAGE_URL } from '../../../constants'
import { useAppDispatch, useAppSelector } from '../../../redux-store/hooks'
import { createProduct, getFileUploadURL } from '../../../services'
import {
  ProductDiscount,
  ProductExtrasForm,
  ProductInstructionsForm,
  ProductTax,
} from '../components'
import { ProductRates } from '../components/product-rates'

const getDefaultProduct = (): ProductType => {
  const newID = uuid()
  return {
    productId: newID,
    productCode: 1001, // Default starting code
    name: '',
    description: '',
    categoryId: '',
    category: '',
    image: NO_IMAGE_URL,
    defaults: new Map(),
    rateCard: new Map(),
    isInStock: true,
    currency: CURRENCY.RUPEE,
    tax: {
      isTaxApplicable: false,
      enableStrictRules: false,
      enableProductTax: false,
      applyDefaultTax: false,
      appliedComponent: [],
    },
    discounts: {
      enableStrictRules: false,
      isDiscountApplicable: false,
      enableProductDiscount: false,
      allowTotalBillDiscount: false,
      applyDefaultDiscount: false,
      appliedComponent: [],
    },
    productInstructions: {},
    productExtras: {},
  }
}

interface AddPopupProps {
  exit?: () => void
}

const AddProductPopup: FC<AddPopupProps> = ({ exit }) => {
  const theme = useTheme()
  const dispatcher = useAppDispatch()
  const categories = useAppSelector((state) => state.categories.data)

  const [loading, setLoading] = useState(false)
  const [imgLoading, setImageLoading] = useState(false)
  const [tabValue, setTabValue] = useState('1')
  const [product, setProduct] = useState<ProductType>(getDefaultProduct())
  const [instructions, setInstructions] = useState<IProductInstructions>({})
  const [extras, setExtras] = useState<IProductExtras>({})

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue)
  }

  const createNewProduct = async () => {
    try {
      setLoading(true)
      const newProduct: ProductType = {
        ...product,
        rateCard: Object.fromEntries(product.rateCard),
        defaults: Object.fromEntries(product.defaults),
        productInstructions: instructions,
        productExtras: extras,
      }
      const resp = await dispatcher(createProduct(newProduct))
      if (resp && resp.type?.includes('fulfilled')) {
        setProduct(getDefaultProduct())
        if (exit) exit()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const uploadImageToBucket = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setImageLoading(true)
      const newFile = event.target.files?.[0]
      if (newFile) {
        const imageURL = await getFileUploadURL(newFile)
        setProduct({ ...product, image: imageURL })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setImageLoading(false)
    }
  }

  if (loading) {
    return (
      <Stack
        sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography sx={{ mt: 2, fontWeight: 600, color: 'text.secondary' }}>
          Creating Product...
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={0} sx={{ height: '100%', bgcolor: 'background.default' }}>
      {/* --- Header Section --- */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 1,
              borderRadius: '12px',
              bgcolor: theme.palette.success.main + '15',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <AddCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              New Product
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fill in the details to expand your menu
            </Typography>
          </Box>
        </Stack>

        <CommonBtn
          variant="contained"
          onClick={createNewProduct}
          disabled={!product.name}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveAsIcon />}
          sx={{ borderRadius: '10px', px: 3 }}
        >
          Create Product
        </CommonBtn>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Grid container spacing={4}>
          {/* --- Image Section --- */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                position: 'relative',
                height: 280,
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: theme.shadows[8],
                bgcolor: theme.palette.grey[100],
                border: `2px dashed ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  backgroundImage: `url(${product?.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: product?.image === NO_IMAGE_URL ? 0.5 : 1,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                }}
              >
                <LoadingButton
                  component="label"
                  variant="contained"
                  loading={imgLoading}
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  {product?.image === NO_IMAGE_URL
                    ? 'Upload Product Image'
                    : 'Change Image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={uploadImageToBucket}
                  />
                </LoadingButton>
              </Box>
            </Box>
          </Grid>

          {/* --- Basic Info Section --- */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Product Name"
                placeholder="Enter product name..."
                variant="outlined"
                value={product.name}
                onChange={(e) =>
                  setProduct({ ...product, name: e.target.value })
                }
                InputProps={{ sx: { borderRadius: '12px' } }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Internal Code"
                    type="number"
                    value={product.productCode}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        productCode: parseInt(e.target.value, 10),
                      })
                    }
                    InputProps={{ sx: { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Select
                    fullWidth
                    value={product.isInStock ? 'stock' : 'outOfStock'}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        isInStock: e.target.value === 'stock',
                      })
                    }
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="stock">Initially Available</MenuItem>
                    <MenuItem value="outOfStock">
                      Initially Out of Stock
                    </MenuItem>
                  </Select>
                </Grid>
              </Grid>

              <Autocomplete
                fullWidth
                options={categories}
                getOptionLabel={(option) => option.categoryName}
                value={
                  categories.find((c) => c.categoryId === product.categoryId) ||
                  null
                }
                onChange={(_, newValue) => {
                  setProduct({
                    ...product,
                    categoryId: newValue?.categoryId || '',
                    category: newValue?.categoryName || '',
                  })
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign Category"
                    required
                    InputProps={{
                      ...params.InputProps,
                      sx: { borderRadius: '12px' },
                    }}
                  />
                )}
              />
            </Stack>
          </Grid>

          {/* --- Config Tabs --- */}
          <Grid item xs={12}>
            <TabContext value={tabValue}>
              <Box
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: '16px',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                }}
              >
                <TabList
                  onChange={handleTabChange}
                  variant="scrollable"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTabs-indicator': { height: 3 },
                  }}
                >
                  <Tab
                    label="Pricing"
                    value="1"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Tax"
                    value="2"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Offers"
                    value="3"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Instructions"
                    value="4"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Extras"
                    value="5"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                </TabList>

                <Box sx={{ minHeight: 200 }}>
                  <TabPanel value="1">
                    <ProductRates
                      allRate={product.rateCard}
                      setAllRate={(rate, def) =>
                        setProduct({
                          ...product,
                          rateCard: rate,
                          defaults: def,
                        })
                      }
                    />
                  </TabPanel>
                  <TabPanel value="2">
                    <ProductTax
                      data={product.tax}
                      onChange={(data) => setProduct({ ...product, tax: data })}
                    />
                  </TabPanel>
                  <TabPanel value="3">
                    <ProductDiscount
                      data={product.discounts}
                      onChange={(data) =>
                        setProduct({ ...product, discounts: data })
                      }
                    />
                  </TabPanel>
                  <TabPanel value="4">
                    <ProductInstructionsForm
                      instructions={instructions}
                      setInstructions={setInstructions}
                    />
                  </TabPanel>
                  <TabPanel value="5">
                    <ProductExtrasForm extras={extras} setExtras={setExtras} />
                  </TabPanel>
                </Box>
              </Box>
            </TabContext>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

export { AddProductPopup }
